import {
  applyEntitySchema,
  objectSchema,
  openUnion,
  optionalTolerant,
  parseEntity,
  sourceJson,
} from '../../_utils/fcdc-v2'
import * as z from 'zod'

export enum EquipmentType {
  Column = 'Column',
  PaymentTerminal = 'PaymentTerminal',
}

export enum RecipientType {
  Email = 'Email',
  SMS = 'SMS',
}

export enum AlertLevelType {
  AnyOffline = 'AnyOffline',
  SomeOffline = 'SomeOffline',
  AllOffline = 'AllOffline',
}

export enum AlertThresholdType {
  Count = 'Count',
}

const dateValue = optionalTolerant(z.union([z.string(), z.number()]))

const equipmentSchema = objectSchema({
  type: optionalTolerant(z.string()),
})

const alertThresholdSchema = openUnion('type', {
  [AlertThresholdType.Count]: objectSchema({
    type: z.literal(AlertThresholdType.Count),
    count: optionalTolerant(z.number()),
  }),
})

const alertLevelSchema = openUnion('type', {
  [AlertLevelType.AnyOffline]: objectSchema({
    type: z.literal(AlertLevelType.AnyOffline),
  }),
  [AlertLevelType.SomeOffline]: objectSchema({
    type: z.literal(AlertLevelType.SomeOffline),
    threshold: optionalTolerant(z.unknown()),
  }),
  [AlertLevelType.AllOffline]: objectSchema({
    type: z.literal(AlertLevelType.AllOffline),
  }),
})

const recipientSchema = openUnion('type', {
  [RecipientType.Email]: objectSchema({
    type: z.literal(RecipientType.Email),
    subscriptions: optionalTolerant(z.array(z.unknown())),
    emailAddress: optionalTolerant(z.string()),
  }),
  [RecipientType.SMS]: objectSchema({
    type: z.literal(RecipientType.SMS),
    subscriptions: optionalTolerant(z.array(z.unknown())),
    phoneNumber: optionalTolerant(z.string()),
  }),
})

const subscriptionSchema = objectSchema({
  equipment: optionalTolerant(z.unknown()),
  alertLevels: optionalTolerant(z.array(z.unknown())),
})

const equipmentSnapshotSchema = objectSchema({
  type: optionalTolerant(z.string()),
  total: optionalTolerant(z.number()),
  offlineIds: optionalTolerant(z.array(z.string())),
})

const locationSnapshotSchema = objectSchema({
  locationId: optionalTolerant(z.string()),
  equipment: optionalTolerant(z.array(z.unknown())),
  recipients: optionalTolerant(z.array(z.unknown())),
  created: dateValue,
  modified: dateValue,
})

const snapshotSchema = objectSchema({
  locations: optionalTolerant(z.array(z.unknown())),
})

export class Equipment {
  type: EquipmentType

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    this.type = source.type
    applyEntitySchema(this, equipmentSchema, source, {})
  }

  static fromJson(json: any): Equipment {
    return new Equipment(json ?? {})
  }
}

export class WatchdogSubscription {
  equipment: Equipment
  alertLevels: AlertLevel[]

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    const equipment = sourceJson(source.equipment)
    const alertLevels = Array.isArray(source.alertLevels)
      ? source.alertLevels.map((level: unknown) => sourceJson(level))
      : undefined
    const plain: Record<string, unknown> = { ...source, equipment }
    if (Array.isArray(alertLevels)) {
      plain['alertLevels'] = alertLevels
    }
    this.equipment = Equipment.fromJson(equipment)
    this.alertLevels = Array.isArray(alertLevels)
      ? alertLevels.map(level => AlertLevel.fromJson(level))
      : []
    applyEntitySchema(this, subscriptionSchema, plain, { alertLevels: [] })
  }
}

export abstract class Recipient {
  type: RecipientType
  subscriptions: WatchdogSubscription[]

  constructor(json: any, type: RecipientType) {
    const source = sourceJson(json ?? {}) as any
    const subscriptions = Array.isArray(source.subscriptions)
      ? source.subscriptions.map((subscription: unknown) => sourceJson(subscription))
      : undefined
    const plain: Record<string, unknown> = { ...source }
    if (Array.isArray(subscriptions)) {
      plain['subscriptions'] = subscriptions
    }
    this.type = type
    this.subscriptions = Array.isArray(subscriptions)
      ? subscriptions.map(subscription => new WatchdogSubscription(subscription))
      : []
    applyEntitySchema(this, recipientSchema, plain, { subscriptions: [] })
  }

  static fromJson(json: any): Recipient {
    switch (json?.type) {
      case RecipientType.Email:
        return new EmailRecipient(json)
      case RecipientType.SMS:
        return new SmsRecipient(json)
      default:
        return parseEntity(recipientSchema, json ?? {}) as unknown as Recipient
    }
  }
}

export class EmailRecipient extends Recipient {
  emailAddress: string

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    super(source, RecipientType.Email)
    this.emailAddress = source.emailAddress
  }
}

export class SmsRecipient extends Recipient {
  phoneNumber: string

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    super(source, RecipientType.SMS)
    this.phoneNumber = source.phoneNumber
  }
}

export abstract class AlertLevel {
  type: AlertLevelType

  constructor(type: AlertLevelType) {
    this.type = type
  }

  static fromJson(json: any): AlertLevel {
    switch (json?.type) {
      case AlertLevelType.AnyOffline:
        return new AnyOfflineAlertLevel(json)
      case AlertLevelType.SomeOffline:
        return new SomeOfflineAlertLevel(json)
      case AlertLevelType.AllOffline:
        return new AllOfflineAlertLevel(json)
      default:
        return parseEntity(alertLevelSchema, json ?? {}) as unknown as AlertLevel
    }
  }
}

export class AnyOfflineAlertLevel extends AlertLevel {
  constructor(json: any = {}) {
    super(AlertLevelType.AnyOffline)
    applyEntitySchema(this, alertLevelSchema, sourceJson(json ?? {}), {})
  }
}

export class SomeOfflineAlertLevel extends AlertLevel {
  threshold: AlertThreshold

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    const threshold = sourceJson(source.threshold)
    super(AlertLevelType.SomeOffline)
    this.threshold = AlertThreshold.fromJson(threshold)
    applyEntitySchema(this, alertLevelSchema, { ...source, threshold }, {})
  }
}

export class AllOfflineAlertLevel extends AlertLevel {
  constructor(json: any = {}) {
    super(AlertLevelType.AllOffline)
    applyEntitySchema(this, alertLevelSchema, sourceJson(json ?? {}), {})
  }
}

export abstract class AlertThreshold {
  type: AlertThresholdType

  constructor(type: AlertThresholdType) {
    this.type = type
  }

  static fromJson(json: any): AlertThreshold {
    switch (json?.type) {
      case AlertThresholdType.Count:
        return new CountAlertThreshold(json)
      default:
        return parseEntity(alertThresholdSchema, json ?? {}) as unknown as AlertThreshold
    }
  }
}

export class CountAlertThreshold extends AlertThreshold {
  count: number

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    super(AlertThresholdType.Count)
    this.count = source.count
    applyEntitySchema(this, alertThresholdSchema, source, {})
  }
}

export class EquipmentSnapshot {
  type: EquipmentType
  total: number
  offlineIds: string[]

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    this.type = source.type
    this.total = source.total ?? 0
    this.offlineIds = source.offlineIds ?? []
    applyEntitySchema(this, equipmentSnapshotSchema, source, { total: 0, offlineIds: [] })
  }

  static fromJson(json: any): EquipmentSnapshot {
    return new EquipmentSnapshot(json ?? {})
  }
}

export class LocationSnapshot {
  locationId: string
  equipment: EquipmentSnapshot[]
  recipients: Recipient[]
  created: Date
  modified: Date

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    const equipment = Array.isArray(source.equipment)
      ? source.equipment.map((item: unknown) => sourceJson(item))
      : undefined
    const recipients = Array.isArray(source.recipients)
      ? source.recipients.map((item: unknown) => sourceJson(item))
      : undefined
    const plain: Record<string, unknown> = { ...source }
    if (Array.isArray(equipment)) {
      plain['equipment'] = equipment
    }
    if (Array.isArray(recipients)) {
      plain['recipients'] = recipients
    }
    this.locationId = source.locationId
    this.equipment = Array.isArray(equipment)
      ? equipment.map((item: unknown) => EquipmentSnapshot.fromJson(item))
      : []
    this.recipients = Array.isArray(recipients)
      ? recipients.map((item: unknown) => Recipient.fromJson(item))
      : []
    this.created = new Date(source.created)
    this.modified = new Date(source.modified)
    applyEntitySchema(this, locationSnapshotSchema, plain, { equipment: [], recipients: [] })
  }
}

export class Snapshot {
  locations: LocationSnapshot[]

  constructor(json: any) {
    const source = sourceJson(json ?? {}) as any
    const locations = Array.isArray(source.locations)
      ? source.locations.map((location: unknown) => sourceJson(location))
      : undefined
    const plain: Record<string, unknown> = { ...source }
    if (Array.isArray(locations)) {
      plain['locations'] = locations
    }
    this.locations = Array.isArray(locations)
      ? locations.map((location: unknown) => new LocationSnapshot(location))
      : []
    applyEntitySchema(this, snapshotSchema, plain, { locations: [] })
  }
}
